
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { type Product } from "@/components/services/products/types";

export const useProductsData = () => {
  const { data: products, isLoading: isProductsLoading } = useQuery({
    queryKey: ["professional-products"],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");

        const { data: productsData, error } = await supabase
          .from("professional_products")
          .select(`
            *,
            profiles:professional_id (
              full_name, 
              profile_photo, 
              rating, 
              response_rate, 
              response_time,
              total_reviews,
              practice_address,
              specialties,
              about_me,
              experience,
              education,
              languages,
              certifications
            )
          `)
          .eq("professional_id", user.id)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching products:", error);
          toast.error("Erreur lors de la récupération des produits");
          return [];
        }
        
        console.log("Raw products data:", productsData);
        
        // Transform data to match the required Product type
        const transformedProducts: Product[] = productsData.map(product => {
          // Extract existing properties safely
          const rawProduct = product as any; // Use any temporarily to access dynamic properties
          
          // Vérifier et nettoyer les URLs d'images
          const photoUrl = rawProduct.photo_url || "";
          const photos = Array.isArray(rawProduct.photos) ? 
            rawProduct.photos.filter(url => typeof url === 'string' && url.trim() !== '') : 
            [];
            
          console.log("Product photo URL:", photoUrl);
          console.log("Product photos array:", photos);
          
          // Parse quotes from JSON if it's a string
          let parsedQuotes = [];
          if (rawProduct.quotes) {
            try {
              if (typeof rawProduct.quotes === 'string') {
                parsedQuotes = JSON.parse(rawProduct.quotes);
              } else if (Array.isArray(rawProduct.quotes)) {
                parsedQuotes = rawProduct.quotes;
              } else if (typeof rawProduct.quotes === 'object') {
                parsedQuotes = [rawProduct.quotes];
              }
            } catch (e) {
              console.error("Error parsing quotes:", e);
              parsedQuotes = [];
            }
          }
          
          return {
            ...product,
            // Ensure all required fields are present
            address: product.address || "",
            phone: product.phone || "",
            photo_url: photoUrl,
            photos: photos,
            condition: product.condition || "",
            quotes: parsedQuotes,
            currency: rawProduct.currency || "EUR",
            profiles: product.profiles || {
              full_name: "",
              profile_photo: "",
              rating: 0,
              response_rate: 0,
              response_time: 0,
              total_reviews: 0,
              practice_address: "",
              specialties: [],
              about_me: null,
              experience: null,
              education: null,
              languages: [],
              certifications: []
            }
          };
        });
        
        console.log("Transformed products:", transformedProducts);
        return transformedProducts;
      } catch (error) {
        console.error("Error in products query:", error);
        toast.error("Erreur lors de la récupération des produits");
        return [];
      }
    },
  });

  return {
    products,
    isProductsLoading
  };
};
